import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import React from "react";

interface GenericInputProps {
  label?: React.ReactNode;
  placeholder?: string;
  value: string | number;
  /**
   * onChange recibe SIEMPRE el valor (ya convertido a string).
   * Puedes luego guardarlo en tu estado con setSearchValue.
   */
  onChange?: (value: string) => void;
  type?: "text" | "password" | "email" | "search" | "number";
  required?: boolean;
  helperText?: string;
  hasError?: boolean;
  icon?: React.ReactNode;
  tooltipText?: string;
  tooltipIcon?: React.ReactNode;
  theme?: "light" | "dark";
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
  readOnly?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}

const GenericInput: React.FC<GenericInputProps> = ({
  label,
  placeholder = "",
  value,
  onChange,
  type = "text",
  required = false,
  helperText = "",
  hasError = false,
  icon,
  tooltipText,
  tooltipIcon,
  theme = "light",
  fullWidth = true,
  disabled = false,
  className = "",
  readOnly = false,
  onKeyDown,
}) => {
  // Estilos básicos de tema
  const themeStyles = {
    light: {
      label: "text-semantic-text-primary",
      input: "bg-white border-[#DCDDE0] text-[#111113]",
      error: "text-red-500 border-red-500",
      helper: "text-gray-500",
      require: "text-semantic-text-brand",
      tooltip: "text-semantic-text-brand",
      icon: "text-semantic-text-brand",
    },
    dark: {
      label: "text-semantic-text-primary",
      input: "bg-[#1E1E1E] border-[#444444] text-[#FAFAFA]",
      error: "text-red-400 border-red-400",
      helper: "text-gray-400",
      require: "text-semantic-text-brand",
      tooltip: "text-semantic-text-brand",
      icon: "text-semantic-text-brand",
    },
  };

  // Siempre pasamos sólo el e.target.value (como string) al callback
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  // Estilos para readOnly
  const readOnlyStyles =
    readOnly && !disabled
      ? "bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed"
      : "";

  return (
    <div className={`flex flex-col space-y-2 ${className}`}>
      {/* Label */}
      {label && (
        <div className="flex items-center space-x-1">
          <label className={`text-sm font-medium ${themeStyles[theme].label}`}>
            {label}
            {required && (
              <span className={`ml-1 ${themeStyles[theme].require}`}>*</span>
            )}
          </label>
          {tooltipText && (
            <GenericTooltip
              text={tooltipText}
              position="top"
              icon={tooltipIcon}
              
            />
          )}
        </div>
      )}

      {/* Input wrapper */}
      <div className="relative">
        {/* Icono opcional */}
        {icon && (
          <span
            className={`
              absolute left-2 top-1/2 transform -translate-y-1/2
              ${themeStyles[theme].icon}
            `}
          >
            {icon}
          </span>
        )}

        <input
          type={type}
          placeholder={placeholder}
          value={String(value)}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          disabled={disabled}
          readOnly={readOnly}
          className={`
            py-2 px-3 rounded-sm border transition-all duration-200
            ${themeStyles[theme].input}
            ${hasError ? themeStyles[theme].error : ""}
            ${icon ? "pl-10" : ""}
            ${disabled ? "opacity-50 cursor-not-allowed" : ""}
            ${readOnlyStyles}
            ${fullWidth ? "w-full" : ""}
          `}
        />
      </div>

      {/* Helper text o error */}
      {helperText && (
        <p
          className={`
            text-xs
            ${hasError ? themeStyles[theme].error : themeStyles[theme].helper}
          `}
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

export default GenericInput;
